import React, { useState } from 'react';
import { type ModalFuncProps, ModalProps } from 'antd';

import { Flex } from '@ui-library/flex';
import { Button } from '@ui-library/button';

type CommonModalPropsPicks =
  | 'onOk'
  | 'onCancel'
  | 'okText'
  | 'cancelText'
  | 'okButtonProps'
  | 'cancelButtonProps';

/**
 * Footer for imperative confirm dialogs (useModal().confirm).
 * Manages its own loading state and closes the modal via destroyRef.
 */
export const NextConfirmFooter: React.FC<
  Pick<ModalFuncProps, CommonModalPropsPicks> & {
    destroyRef: React.RefObject<(() => void) | undefined>;
  }
> = ({
  onOk,
  onCancel,
  okText = 'OK',
  cancelText = 'Cancel',
  okButtonProps,
  cancelButtonProps,
  destroyRef,
}) => {
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    try {
      const result = onOk?.();
      if (result instanceof Promise) {
        setLoading(true);
        await result;
      }
      destroyRef.current?.();
    } catch {
      // onOk rejected — keep modal open (matches Ant behavior)
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    destroyRef.current?.();
  };

  return (
    <Flex justifyContent="flex-end" gap="8px">
      <Button
        {...cancelButtonProps}
        variant="next"
        level="secondary"
        size="middle"
        onClick={handleCancel}
        data-testid="btnCancel"
        disabled={cancelButtonProps?.disabled || loading}
      >
        {cancelText}
      </Button>
      <Button
        {...okButtonProps}
        variant="next"
        level="primary"
        intent={okButtonProps?.danger ? 'destructive' : 'default'}
        size="middle"
        onClick={handleOk}
        loading={loading}
        data-testid="btnSave"
        danger={false}
      >
        {okText}
      </Button>
    </Flex>
  );
};

/**
 * Footer with ui-library Button components for the declarative Modal.
 */
export const NextFooter: React.FC<Pick<ModalProps, CommonModalPropsPicks | 'confirmLoading'>> = ({
  onOk,
  onCancel,
  okText = 'OK',
  cancelText = 'Cancel',
  okButtonProps,
  cancelButtonProps,
  confirmLoading,
}) => (
  <Flex justifyContent="flex-end" gap="8px">
    <Button
      {...cancelButtonProps}
      variant="next"
      level="secondary"
      size="middle"
      onClick={onCancel as React.MouseEventHandler<HTMLElement>}
      data-testid="btnCancel"
      disabled={cancelButtonProps?.disabled || confirmLoading}
    >
      {cancelText}
    </Button>
    <Button
      {...okButtonProps}
      variant="next"
      level="primary"
      intent={okButtonProps?.danger ? 'destructive' : 'default'}
      size="middle"
      onClick={onOk as React.MouseEventHandler<HTMLElement>}
      loading={confirmLoading}
      data-testid="btnSave"
      danger={false}
    >
      {okText}
    </Button>
  </Flex>
);
